import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';

import { bookingShape, notesShape } from 'constants/propTypes';
import { GET_USER, GET_SALESFORCE_CASES } from 'store/queries';
import { isLoungeStaff, isUserCallCenterAgent, isUserStationAgent } from 'utils/user';

import NoteDetails from './NoteDetails';
import HistoryDetails from './HistoryDetails';
import DatesDetails from './DatesDetails';
import SalesforceCasesDetails from './SalesforceCasesDetails';
import { getCasesCounts } from './SalesforceCasesDetails/SalesforceCasesDetails.utils';
import { Heading, Title, StyledTabs } from './NotesAndHistorySection.styles';

const NotesAndHistorySection = ({ booking, defaultTab, bookingReference, notes, dates, bookingStatus }) => {
  const [selectedTab, handleTabSelect] = useState(defaultTab || 'notes');

  const { data: userData } = useQuery(GET_USER);
  const user = userData?.me;
  const isUserEligible = (isUserStationAgent(user) || isUserCallCenterAgent(user)) && !isLoungeStaff(user) && !user?.isReadOnly;

  const {
    data: salesforceCasesData,
    loading: salesforceCasesLoading,
    error: salesforceCasesError
  } = useQuery(GET_SALESFORCE_CASES, {
    variables: { bookingReference },
    fetchPolicy: 'network-only',
    skip: !isUserEligible
  });

  const salesforceCases = salesforceCasesData?.getSalesforceCases ?? [];
  const salesforceCasesCounts = getCasesCounts(salesforceCases);
  const currentTab = selectedTab === 'salesforce' && !isUserEligible ? 'notes' : selectedTab;

  const salesforceTabLabel = salesforceCasesError ? (
    <FormattedMessage id="notes.tabSalesforceCases" values={{ total: '–' }} />
  ) : (
    <FormattedMessage
      id={salesforceCasesCounts.active ? 'notes.tabSalesforceCasesWithActive' : 'notes.tabSalesforceCases'}
      values={salesforceCasesCounts}
    />
  );

  return (
    <>
      <Heading>
        <Title>
          <FormattedMessage id="notes.title" />
        </Title>

        <StyledTabs
          onClick={handleTabSelect}
          selectedValue={currentTab}
          tabs={[
            {
              value: 'notes',
              label: <FormattedMessage id="notes.tabNotes" />
            },
            {
              value: 'history',
              label: <FormattedMessage id="notes.tabHistory" />
            },
            {
              value: 'dates',
              label: <FormattedMessage id="notes.dates" />
            },
            ...(isUserEligible
              ? [
                  {
                    value: 'salesforce',
                    label: salesforceTabLabel
                  }
                ]
              : [])
          ]}
        />
      </Heading>

      {currentTab === 'history' && <HistoryDetails booking={booking} bookingReference={bookingReference} />}
      {currentTab === 'notes' && <NoteDetails bookingReference={bookingReference} notes={notes} />}
      {currentTab === 'dates' && <DatesDetails dates={dates} bookingReference={bookingReference} bookingStatus={bookingStatus} />}
      {currentTab === 'salesforce' && (
        <SalesforceCasesDetails cases={salesforceCases} loading={salesforceCasesLoading} error={salesforceCasesError} />
      )}
    </>
  );
};

NotesAndHistorySection.propTypes = {
  booking: bookingShape,
  notes: notesShape.isRequired,
  defaultTab: PropTypes.string,
  bookingReference: PropTypes.string.isRequired,
  dates: PropTypes.shape({
    confirmedTimestamp: PropTypes.string,
    cancelledTimestamp: PropTypes.string,
    expiryTimestamp: PropTypes.string,
    voidExpiryDate: PropTypes.string,
    createTimestamp: PropTypes.string,
    updatedTimestamp: PropTypes.string
  }),
  bookingStatus: PropTypes.string
};

export default NotesAndHistorySection;
